import React, { useRef, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { FormatUSD } from "../../../utils/helpers/string";
import moment from "moment";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  Tooltip,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

// Register ChartJS components, custom plugin, and zoom plugin
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  Tooltip,
  zoomPlugin,
  {
    id: "highlightPlugin",
    afterDraw: (chart) => {
      const { ctx, tooltip } = chart;
      if (!tooltip || !tooltip.dataPoints || tooltip.dataPoints.length === 0)
        return;

      const dataPoint = tooltip.dataPoints[0];
      const xAxis = chart.scales.x;
      const yAxis = chart.scales.y;

      const x = dataPoint.element.x;
      const y = dataPoint.element.y;

      // Vertical dashed line
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, yAxis.top);
      ctx.lineTo(x, yAxis.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#fff";
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.restore();

      // Horizontal dashed line
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(xAxis.left, y);
      ctx.lineTo(xAxis.right, y);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#fff";
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.restore();

      const drawBoxWithText = (
        text,
        posX,
        posY,
        align = "center",
        isXAxisLabel = false
      ) => {
        ctx.save();
        ctx.font = "bold 12px Nunito Sans";
        const textWidth = ctx.measureText(text).width;
        const textHeight = 16;
        const padding = 8;
        const boxWidth = textWidth + padding * 2;
        const boxHeight = textHeight + padding;

        let boxX = posX;
        let boxY = posY;

        if (isXAxisLabel) {
          const chartWidth = chart.width;
          if (boxX - boxWidth / 2 < 0) {
            boxX = padding + boxWidth / 2;
          } else if (boxX + boxWidth / 2 > chartWidth) {
            boxX = chartWidth - padding - boxWidth / 2;
          }
        } else {
          boxX = align === "center" ? posX - boxWidth / 2 : posX;
        }

        ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
        ctx.shadowBlur = 6;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;

        ctx.fillStyle = "#EDE9FE";
        ctx.beginPath();
        ctx.roundRect(boxX - boxWidth / 2, boxY, boxWidth, boxHeight, 8);
        ctx.fill();

        ctx.fillStyle = "#000";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, boxX, boxY + boxHeight / 2);
        ctx.restore();
      };

      drawBoxWithText(dataPoint.label, x, yAxis.bottom + 13, "center", true);
      drawBoxWithText(
        FormatUSD(dataPoint.raw),
        xAxis.right - 100,
        y - 10,
        "left"
      );
    },
  }
);

const getLastAvailableDateWithIndex = (data, currentDate) => {
  const currentTime = currentDate.getTime();

  return data.reduce(
    (acc, item, index) => {
      const itemTime = new Date(item.created_at).getTime();

      if (itemTime < currentTime) {
        if (acc.time === null || itemTime > acc.time) {
          return { item, index, time: itemTime };
        }
      }

      return acc;
    },
    { item: null, index: -1, time: null }
  );
};


function TradingGrowthChart({ balance_chart, maxLoss, profit, newMaxLoss }) {
  const chartRef = useRef(null);
  const containerRef = useRef(null); // Ref for the container div
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [gradients, setGradients] = useState({
    balanceGradient: null,
    profitGradient: null,
    maxLossGradient: null,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 500);

  // Calculate min and max values from all datasets
  const calculateYAxisLimits = (datasets) => {
    let min = Infinity;
    let max = -Infinity;

    datasets.forEach(dataset => {
      if (dataset.data && dataset.data.length > 0) {
        const datasetMin = Math.min(...dataset.data);
        const datasetMax = Math.max(...dataset.data);
        min = Math.min(min, datasetMin);
        max = Math.max(max, datasetMax);
      }
    });

    // Add padding of 10% to the range
    const range = max - min;
    const padding = range * 0.1;

    return {
      min: Math.floor(min - padding),
      max: Math.ceil(max + padding)
    };
  };

  // Handle window resize to update isMobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 500);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const output = [];
    let count = 0;
    let initialDayCondition = true;
    const timeData = balance_chart?.map((entry) => entry.time) || [];
    let choice = 0;

    // Calculate max loss limits
    if (Array.isArray(newMaxLoss) && newMaxLoss.length) {
      for (let i = 0; i < timeData.length; i++) {
        const currentTradeDate = new Date(timeData[i]);

        const currentMonth = currentTradeDate.getUTCMonth();
        const currentYear = currentTradeDate.getUTCFullYear();

        const currentMaxLossDate = new Date(newMaxLoss[count]?.created_at);
        const maxLossMonth = currentMaxLossDate.getUTCMonth();
        const maxLossYear = currentMaxLossDate.getUTCFullYear();

        // 1 : Trade date is before maxloss month/year
        if (
          currentYear < maxLossYear ||
          (currentYear === maxLossYear && currentMonth < maxLossMonth)
        ) {
          output.push(newMaxLoss[count]?.maximum_loss_limit);
          continue;
        }

        // 2 : Find nearest smaller date in same month & year
        const { item, index } = getLastAvailableDateWithIndex(
          newMaxLoss,
          currentTradeDate
        );

        if (item) {
          output.push(item.maximum_loss_limit);
          count = index;
        } else {
          output.push(newMaxLoss[count]?.maximum_loss_limit);
        }
      }
    }

    if (newMaxLoss && output.length < timeData.length) {
      for (let i = output.length; i < timeData.length; i++) {
        output.push(maxLoss);
      }
    }

    const labels =
      balance_chart?.map((d) => {
        return moment(d.time).utc().format("DD MMM YY");
      }) || [];

    const chartInstance = chartRef.current;
    const gradientHeight = 100;

    if (chartInstance && !gradients.balanceGradient) {
      const ctx = chartInstance.ctx;

      const balanceGradient = ctx.createLinearGradient(0, 0, 0, gradientHeight);
      balanceGradient.addColorStop(0, "rgba(5, 255, 0, 0.8)");
      balanceGradient.addColorStop(1, "rgba(196, 181, 253, 0)");

      const profitGradient = ctx.createLinearGradient(0, 0, 0, gradientHeight);
      profitGradient.addColorStop(0, "rgba(255, 251, 0, 0.26)");
      profitGradient.addColorStop(1, "rgba(196, 181, 253, 0)");

      const maxLossGradient = ctx.createLinearGradient(0, 0, 0, gradientHeight);
      maxLossGradient.addColorStop(0, "rgba(255, 0, 0, 0.8)");
      maxLossGradient.addColorStop(1, "rgba(196, 181, 253, 0)");

      setGradients({
        balanceGradient,
        profitGradient,
        maxLossGradient,
      });
    }

    const datasets = [
      {
        label: "Balance",
        data: balance_chart?.map((d) => d.balance) || [],
        items: balance_chart,
        borderColor: "rgba(5, 255, 0, 0.8)",
        backgroundColor: gradients.balanceGradient || "rgba(5, 255, 0, 0.2)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "Profit target",
        data: balance_chart?.map(() => profit) || [],
        items: balance_chart,
        borderColor: "rgba(255, 250, 0, 0.8)",
        backgroundColor: gradients.profitGradient || "rgba(255, 250, 0, 0.2)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "Max loss limit",
        data: output,
        items: balance_chart,
        borderColor: "rgba(255, 0, 0, 0.8)",
        backgroundColor: gradients.maxLossGradient || "rgba(255, 0, 0, 0.2)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ];

    const yAxisLimits = calculateYAxisLimits(datasets);
    setChartData({ labels, datasets, yAxisLimits });

    return () => {
      setChartData({ labels: [], datasets: [] });
      // Cleanup custom tooltip on unmount
      const tooltipEl = document.getElementById("chartjs-tooltip");
      if (tooltipEl) tooltipEl.remove();
    };
  }, [balance_chart, newMaxLoss, maxLoss, profit, gradients]);

  const calculateMaxTicksLimit = (dataLength) => {
    if (dataLength <= 10) return dataLength;
    if (dataLength <= 50) return 10;
    if (dataLength <= 100) return 8;
    return 6;
  };

  const customTooltip = (context) => {
    let tooltipEl = document.getElementById("chartjs-tooltip");

    // Create tooltip element if it doesn't exist
    if (!tooltipEl && containerRef.current) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "chartjs-tooltip";
      tooltipEl.style.background = "rgba(0, 0, 0, 0.8)";
      tooltipEl.style.borderRadius = "8px";
      tooltipEl.style.color = "#FFF";
      tooltipEl.style.padding = "10px";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.fontFamily = "Nunito Sans";
      tooltipEl.style.fontSize = "12px";
      tooltipEl.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.2)";
      containerRef.current.appendChild(tooltipEl); // Append to container div
    }

    const { chart, tooltip } = context;

    // Hide if no tooltip data
    if (tooltip.opacity === 0) {
      if (tooltipEl) tooltipEl.style.opacity = "0";
      return;
    }

    // Build tooltip content
    if (tooltip.body) {
      const dataPoints = tooltip.dataPoints;
      const dataIndex = dataPoints[0].dataIndex;
      const time = balance_chart?.[dataIndex]?.time;
      const dateTime = time ? moment(time).utc().format("DD MMM YY HH:mm:ss") : "N/A";

      let content = `<div style="font-weight: 600; margin-bottom: 5px; font-family:Nunito Sans;">${dateTime}</div>`;
      content += "<div>";

      dataPoints.forEach((dataPoint) => {
        const { dataset, raw } = dataPoint;
        const value = FormatUSD(raw);
        const color = dataset.borderColor;
        content += `
          <div style="display: flex; align-items: center; margin: 2px 0;">
            <span style="width: 12px; height: 12px; background-color: ${color}; border-radius: 50%; margin-right: 6px;"></span>
            <span style="font-family:Nunito Sans;" >${dataset.label}: ${value}</span>
          </div>
        `;
      });

      content += "</div>";
      tooltipEl.innerHTML = content;
    }

    // Position the tooltip relative to the container div
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const dataPoint = tooltip.dataPoints[0];

    // Use original data point coordinates (not caretX/caretY)
    const x = dataPoint.element.x; // Pixel position on x-axis
    const y = dataPoint.element.y; // Pixel position on y-axis

    // Calculate tooltip position (above the data point)
    let left = x - tooltipRect.width / 2; // Center horizontally
    let top = y - tooltipRect.height - 10; // 10px above point

    // Adjust for top overflow (place below if it would go off-screen)
    if (top < 0) {
      top = y + 10; // Place below point
    }

    // Adjust for left/right overflow (keep within container bounds)
    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = 360; // Fixed height from div style
    if (left < 0) {
      left = 0;
    } else if (left + tooltipRect.width > containerWidth) {
      left = containerWidth - tooltipRect.width;
    }

    // Adjust for bottom overflow
    if (top + tooltipRect.height > containerHeight) {
      top = y - tooltipRect.height - 10; // Try above again, or clamp
      if (top < 0) top = 0; // Clamp to top if still overflowing
    }

    // Apply position
    tooltipEl.style.opacity = "1";
    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
  };

  // Function to reset zoom
  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
      axis: "x",
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Date",
          color: "#FFF",
          font: {
            size: 12,
            family: "Nunito Sans",
            weight: "600",
          },
        },
        ticks: {
          color: "#FFF",
          font: {
            size: 10,
            family: "Nunito Sans",
            weight: "600",
          },
          autoSkip: true,
          maxTicksLimit: calculateMaxTicksLimit(chartData.labels.length),
          callback: function (value) {
            return moment(this.getLabelForValue(value)).format("DD MMM YY");
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: chartData.yAxisLimits?.min || maxLoss - 5000,
        max: chartData.yAxisLimits?.max || profit + 5000,
        title: {
          display: true,
          text: "Amount (USD)",
          color: "#FFF",
          font: {
            size: 12,
            family: "Nunito Sans",
            weight: "600",
          },
        },
        ticks: {
          color: "#FFF",
          callback: (value) => {
            if (isMobile && Math.abs(value) >= 1000) {
              const formatted = Math.round(Math.abs(value) / 1000);
              return `${value < 0 ? "-" : ""}${"$" + formatted + "K"}`;
            } else {
              return FormatUSD(value);
            }
          },
        },
        grid: {
          drawBorder: false,
          color: "rgba(200, 200, 200, 0)",
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: "#FFF",
          font: {
            size: 12,
            family: "Nunito Sans",
            weight: "600",
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        enabled: false,
        external: customTooltip,
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
            speed: 0.1,
          },
          pinch: {
            enabled: true,
          },
          mode: "xy",
        },
        pan: {
          enabled: true,
          mode: "xy",
        },
        limits: {
          x: { min: "original", max: "original" },
          y: { min: "original", max: "original" },
        },
      },
    },
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", width: "100%", height: "360px" }}
    >
      <Line ref={chartRef} data={chartData} options={options} />
      <button
        onClick={resetZoom}
        style={{
          position: "absolute",
          top: "9px",
          right: "1px",

          padding: "5px 6px",
          backgroundColor: "#4bb390",
          color: "#000",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          height: "max-content",
          display: "grid",
          placeItems: "center",
        }}
      >
        <svg
          fill="#000000"
          width="20px"
          height="20px"
          viewBox="-326.4 -326.4 2572.80 2572.80"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            <path
              d="M960 0v213.333c411.627 0 746.667 334.934 746.667 746.667S1371.627 1706.667 960 1706.667 213.333 1371.733 213.333 960c0-197.013 78.4-382.507 213.334-520.747v254.08H640V106.667H53.333V320h191.04C88.64 494.08 0 720.96 0 960c0 529.28 430.613 960 960 960s960-430.72 960-960S1489.387 0 960 0"
              fillRule="evenodd"
            ></path>
          </g>
        </svg>
      </button>
    </div>
  );
}

export default TradingGrowthChart;
